import React, { useCallback } from "react";

import { faEllipsisH, faLayerGroup, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, flatMap, flatten } from "lodash";
import { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { MarkExtension, MenuExtension, NodeExtension } from "~/lib/prose/extension";
import type { MenuEntry, MenuEntryComponentProps } from "~/lib/prose/extension";
import { groupEntries } from "~/lib/prose/plugins/toolbar";
import { markActive, nodeActive } from "~/lib/prose/utils";
// import { useResizeObserver } from "~/lib/hooks";
import Button, { LabelIconForButton } from "~/ui/button";
import IconButton from "~/ui/button/IconButton";
import Menu from "~/ui/menu";
import Tooltip from "~/ui/Tooltip";

import styles from "./FloatingToolbar.module.css";

export function isActive(entry: MenuEntry, extension: MenuExtension, editorState: EditorState) {
	if (entry.active) return entry.active(editorState);
	const ext = extension.entryExtensions.get(entry);

	if (ext instanceof MarkExtension) {
		return markActive(ext.$type)(editorState);
	} else if (ext instanceof NodeExtension) {
		return nodeActive(ext.name)(editorState);
	}
	return false;
}

export function isDisabled(entry: MenuEntry, editorView: EditorView) {
	return !entry.run()(editorView.state, undefined, editorView);
}

function VerticalRule() {
	return <span className={styles.verticalRule} />;
}

function BlockTypeMenu({
	editorView,
	entries,
	extension,
	hideLabel,
}: {
	editorView: EditorView;
	entries: readonly MenuEntry[];
	extension: MenuExtension;
	hideLabel?: boolean;
}) {
	const { selection, doc } = editorView.state;

	let filteredEntries = entries;
	filteredEntries = entries.filter(entry => entry.group !== "insert");

	const sections = groupEntries(filteredEntries);
	const currentBlockTypeEntry = flatMap(sections.types, ([, entries]) => entries).find(
		e => e.active?.(editorView.state) ?? isActive(e, extension, editorView.state),
	);

	let hasLinkedBlockInSelection = false;
	let hasImageStripInSelection =
		selection.$from.node(1)?.type.name === "image_strip" || selection.$to.node(1)?.type.name === "image_strip";
	doc.slice(selection.from, selection.to).content.descendants(node => {
		if (!hasImageStripInSelection && node.type.name.startsWith("image_strip")) {
			hasImageStripInSelection = true;
		}
		if (!hasLinkedBlockInSelection && node.type.name === "todo" && node.attrs.originalBlockId)
			hasLinkedBlockInSelection = true;
		return false;
	});

	if (hasLinkedBlockInSelection || sections.types.reduce((n, [, { length }]) => n + length, 0) <= 1) return null;

	return (
		<>
			<div className={styles.group}>
				<Menu>
					<Menu.Trigger asChild>
						<Button size="slim" variant="tertiary" className={styles.groupMenu}>
							{currentBlockTypeEntry ? (
								currentBlockTypeEntry.icon && (
									<>
										<LabelIconForButton
											icon={currentBlockTypeEntry.icon}
											fixedWidth
											side="left"
											className={styles.icon}
										/>
										{!hideLabel && currentBlockTypeEntry.title}
									</>
								)
							) : (
								<>
									<LabelIconForButton icon={faLayerGroup} fixedWidth side="left" className={styles.icon} />
									{!hideLabel && "Mixed"}
								</>
							)}
							<LabelIconForButton icon={faCaretDown} fixedWidth side="right" className={styles.caret} />
						</Button>
					</Menu.Trigger>
					<Menu.Content>
						{sections.types.map(([name, entries]) => (
							<React.Fragment key={name}>
								{entries.map(e => (
									<ToolbarItem
										key={e.title}
										asMenuItem
										isMobile={false}
										entry={e}
										extension={extension}
										editorView={editorView}
										active={isActive(e, extension, editorView.state)}
										disabled={(e.greyIfDryRunFalse ?? false) && isDisabled(e, editorView)}
									/>
								))}
							</React.Fragment>
						))}
					</Menu.Content>
				</Menu>
			</div>
			<VerticalRule />
		</>
	);
}

export function ToolbarItem(props: MenuEntryComponentProps) {
	const { asMenuItem: asMenuItemProp, entry, isMobile, editorView, active, disabled } = props;
	const asMenuItem = Boolean(asMenuItemProp);
	const onMenuItemClick = useCallback(
		ev => {
			editorView.focus();
			entry.run()(
				editorView.state,
				tr => {
					editorView.dispatch(tr);
					return true;
				},
				editorView,
				ev,
			);
			ev?.preventDefault();
		},
		[entry, editorView],
	);

	const tooltipText = isMobile || asMenuItem ? null : !!entry.keyboardShortcut ? entry.keyboardShortcut : entry.title;

	const menuEntryToShortcut: { [key: string]: string } = {
		"Talking point": "()",
		"Action item": "[]",
		"Heading 1": "#",
		"Heading 2": "##",
	};

	const toolbarItem = asMenuItem ? (
		<Menu.Item icon={entry.icon ?? faPlus} onSelect={onMenuItemClick}>
			{entry.title}
			{menuEntryToShortcut[entry.title] && (
				<span className={styles.menuItemShortcut}>{menuEntryToShortcut[entry.title]}</span>
			)}
		</Menu.Item>
	) : (
		<Tooltip key={entry.title} content={tooltipText} delay={500} placement="top">
			<IconButton
				variant="tertiary"
				size="small"
				icon={entry.icon}
				onClick={onMenuItemClick}
				className={cx(styles.iconItem, { [styles.linkIconItem]: entry.title === "Link" })}
				data-mark-active={active}
				disabled={disabled}
			/>
		</Tooltip>
	);

	if (entry.component) {
		const Component = entry.component;
		return (
			<Component key={entry.title} {...props}>
				{toolbarItem}
			</Component>
		);
	}

	return toolbarItem;
}

interface FloatingToolbarProps {
	editorView: EditorView;
	entries: readonly MenuEntry[];
	extension: MenuExtension;
	isNarrowWidth: boolean;
}

export default function FloatingToolbar({ editorView, entries, extension, isNarrowWidth }: FloatingToolbarProps) {
	let filteredEntries = entries;
	filteredEntries = entries.filter(entry => entry.group !== "insert");

	const renderItem = useCallback(
		(entry: MenuEntry, isRadixMenu: boolean) => {
			return (
				<ToolbarItem
					key={entry.title}
					asMenuItem={isRadixMenu}
					isMobile={false}
					entry={entry}
					extension={extension}
					editorView={editorView}
					active={isActive(entry, extension, editorView.state)}
					disabled={(entry.greyIfDryRunFalse ?? false) && isDisabled(entry, editorView)}
				/>
			);
		},
		[extension, editorView],
	);

	const renderItems = useCallback(
		(items: MenuEntry[], isRadixMenu: boolean) => {
			return items.map(e => renderItem(e, isRadixMenu));
		},
		[renderItem],
	);

	const renderGroup = useCallback(
		(items: MenuEntry[]) => {
			if (!items || items.length === 0) return;

			return <div className={styles.group}>{renderItems(items, false)}</div>;
		},
		[renderItems],
	);

	const renderMenuGroup = useCallback(
		(sections: MenuEntry[][], numberOfItemsToShowInBar?: number, icon?: IconDefinition) => {
			if (!sections || sections.length === 0) return;

			const itemsToShow = sections[0].splice(0, numberOfItemsToShowInBar ?? 0);
			const groupHasActiveItemInMenu = flatten(sections).some(e => isActive(e, extension, editorView.state));

			return (
				<div className={cx(styles.group)}>
					{!!numberOfItemsToShowInBar && renderItems(itemsToShow, false)}
					<Menu>
						<Menu.Trigger className={styles.groupMenu} data-mark-active={groupHasActiveItemInMenu} asChild>
							{icon ? (
								<Button size="slim" variant="tertiary">
									<FontAwesomeIcon icon={icon} fixedWidth className={styles.icon} />
									<LabelIconForButton icon={faCaretDown} fixedWidth side="right" className={styles.caret} />
								</Button>
							) : (
								<IconButton icon={faEllipsisH} variant="tertiary" size="small" className={styles.iconItem} />
							)}
						</Menu.Trigger>
						<Menu.Content>
							{sections.map((items, index) => (
								<React.Fragment key={items[0].title}>
									{index > 0 && <Menu.Separator />}
									{renderItems(items, true)}
								</React.Fragment>
							))}
						</Menu.Content>
					</Menu>
				</div>
			);
		},
		[renderItems, editorView.state, extension],
	);

	const orderMenuEntriesByPriority = useCallback((items: MenuEntry[]) => {
		return items.sort((a, b) => {
			const aP = a.priority ?? (a.group === "default" ? 1 : 0);
			const bP = b.priority ?? (b.group === "default" ? 1 : 0);
			if (aP > bP) return -1;
			if (aP < bP) return 1;
			return 0;
		});
	}, []);

	const mappedItems = new Map<string, MenuEntry>();

	for (let i = 0; i < filteredEntries.length; i++) {
		mappedItems.set(filteredEntries[i].title, filteredEntries[i]);
	}

	const formattingSection = compact(
		orderMenuEntriesByPriority(filteredEntries.filter(e => e.section === "formatting")),
	);
	const colouringSection = compact(orderMenuEntriesByPriority(filteredEntries.filter(e => e.section === "colouring")));
	const indentSection = compact(orderMenuEntriesByPriority(filteredEntries.filter(e => e.section === "indent")));
	const placeholderEntry = mappedItems.get("Placeholder");
	const linkEntry = mappedItems.get("Link");
	const linkSection = compact([placeholderEntry, linkEntry]);

	return (
		<div className={styles.toolbar}>
			<BlockTypeMenu editorView={editorView} entries={entries} extension={extension} hideLabel={isNarrowWidth} />
			{isNarrowWidth ? (
				renderMenuGroup([formattingSection, linkSection, colouringSection, indentSection], 3)
			) : (
				<>
					{renderMenuGroup([formattingSection], 3)}
					<VerticalRule />
					{renderGroup(linkSection)}
					<VerticalRule />
					{renderGroup(colouringSection)}
					<VerticalRule />
					{renderGroup(indentSection)}
				</>
			)}
		</div>
	);
}
